import { ApiAIPromptStatus, ApiSimpleComponent } from "@incendium/api";
import { AutorenewOutlined, Preview } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import SpacedList from "Components/UI/SpacedList";
import { cell3Icons } from "consts";
import { useConfirmation } from "Hooks/useConfirmation";
import produce from "immer";
import { Suspense, useCallback, useState } from "react";
import LanderPreview from "features/lightningLanders/components/LanderPreview";
import CustomDialog from "Components/CustomDialog/CustomDialog";

interface BlocksListProps {
  blocks: ApiSimpleComponent[];
  setBlocks: (
    updater: (blocks: ApiSimpleComponent[]) => ApiSimpleComponent[],
  ) => void;
  fetched: boolean;
  title: string;
  newButtonLabel: string;
  emptyMessage: string;
  emptySubMessage: string;
  onNew: () => void;
  onEdit: (block: ApiSimpleComponent) => void;
  onDelete: (block: ApiSimpleComponent) => Promise<string>;
}

export function BlocksList({
  blocks,
  setBlocks,
  fetched,
  title,
  newButtonLabel,
  emptyMessage,
  emptySubMessage,
  onNew,
  onEdit,
  onDelete,
}: BlocksListProps) {
  const handleDelete = useConfirmation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [previewBlock, setPreviewBlock] = useState<ApiSimpleComponent | null>(
    null,
  );

  const onPreview = useCallback((block: ApiSimpleComponent) => {
    setPreviewBlock(block);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewBlock(null);
  }, []);

  const handleDeleteClick = useCallback(
    (block: ApiSimpleComponent) => {
      handleDelete({
        title: "Are you sure you want to delete this Block",
        body: `This will not affect any landing page you have created with this block, but it will not be available for new landers`,
        callback: async () => {
          const message = await onDelete(block);
          setBlocks((components) =>
            produce(components, (draft) => {
              // Use externalId for shared blocks, fallback to id for regular blocks
              const blockKey = block.externalId || block.id?.toString();
              const idx = draft.findIndex(
                (d) => (d.externalId || d.id?.toString()) === blockKey,
              );
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            }),
          );
          return message;
        },
      });
    },
    [handleDelete, setBlocks, onDelete],
  );

  return (
    <>
      {!fetched ? (
        <Loading />
      ) : (
        <Suspense fallback={<Loading />}>
          {blocks.length === 0 ? (
            <CenterPage calcHeight>
              <Typography variant="subtitle1">{emptyMessage}</Typography>
              <Typography variant="subtitle1" mb={2} color={"secondary"}>
                {emptySubMessage}
              </Typography>
              <Button onClick={onNew}>{newButtonLabel}</Button>
            </CenterPage>
          ) : (
            <SpacedList
              title={title}
              action={<Button onClick={onNew}>{newButtonLabel}</Button>}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Block Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>AI Processing Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blocks.map((block) => {
                  const isPending =
                    block.prompt?.status ===
                    ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING;
                  return (
                    <TableRow key={block.externalId || block.id}>
                      <TableCell>{block.name}</TableCell>
                      <TableCell>{block.description}</TableCell>
                      <TableCell>
                        {isPending ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "info.main",
                              fontSize: "0.875rem",
                            }}
                          >
                            <AutorenewOutlined
                              sx={{
                                fontSize: 18,
                                animation: "spin 2s linear infinite",
                                "@keyframes spin": {
                                  "0%": { transform: "rotate(0deg)" },
                                  "100%": { transform: "rotate(360deg)" },
                                },
                              }}
                            />
                            <Typography variant="body2" color="info.main">
                              AI processing…
                            </Typography>
                          </Box>
                        ) : block.prompt?.status ===
                          ApiAIPromptStatus.AI_PROMPT_STATUS_COMPLETED ? (
                          <Typography variant="body2">
                            AI processing completed
                          </Typography>
                        ) : block.prompt?.status ===
                          ApiAIPromptStatus.AI_PROMPT_STATUS_FAILED ? (
                          <Typography variant="body2" color="error.main">
                            AI processing failed
                          </Typography>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell align="right" style={{ width: cell3Icons }}>
                        <Tooltip
                          title={
                            isPending
                              ? "Wait for AI to finish"
                              : `Preview ${block.name}`
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => onPreview(block)}
                              disabled={isPending}
                            >
                              <Preview />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            isPending
                              ? "Wait for AI to finish"
                              : `Edit ${block.name}`
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => onEdit(block)}
                              disabled={isPending}
                            >
                              <Edit />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            isPending
                              ? "Wait for AI to finish"
                              : `Delete ${block.name}`
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => handleDeleteClick(block)}
                              disabled={isPending}
                            >
                              <Delete />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </SpacedList>
          )}

          <CustomDialog
            open={!!previewBlock}
            onClose={handleClosePreview}
            fullScreen={fullScreen}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>{previewBlock?.name} - Preview</DialogTitle>
            <DialogContent>
              {previewBlock && (
                <LanderPreview component={previewBlock} height="80vh" />
              )}
            </DialogContent>
          </CustomDialog>
        </Suspense>
      )}
    </>
  );
}
